// Packages
import React from "react";
import { IonModal } from "@ionic/react";
import { autorun, action, computed, observable } from "mobx";
import { inject, observer, disposeOnUnmount } from "mobx-react";
import _ from "lodash";
import { BulbOutlined, CheckOutlined, DeleteOutlined } from "@ant-design/icons";
// Components
import { Card, Alert } from "antd";
import EditActivityForm from "../../../shared/forms/EditActivityForm/EditActivityForm";
import GoalReviewCard from "../GoalReviewCard/GoalReviewCard";
import StepNavigation from "../StepNavigation/StepNavigation";
// Type Definitions
import { IActivity, ICheckIn, IGoal } from "../../../shared/interfaces";
import EditGoalForm from "../../../shared/forms/EditGoalForm/EditGoalForm";
import ActivityCard from "../../../shared/cards/ActivityCard/ActivityCard";
import moment from "moment";
import ActivityForm from "../../../shared/forms/ActivityForm/ActivityForm";
import { activityComparator } from "../../Actions/activityComparator";

interface IProps {
  checkInStore?: any;
  activityStore?: any;
  goalStore?: any;
  checkIn: ICheckIn;
  skipToCheckIn: boolean;
}

@inject("checkInStore", "activityStore", "goalStore")
@observer
class StepTwo extends React.Component<IProps> {
  @observable public loadingCount = 0;
  @computed get goalsNotWorked() {
    return this.props.checkInStore.activeCheckIn.data;
  }

  @computed get emptyGoals() {
    return (
      this.loadingCount > 3 &&
      this.goalsNotWorked &&
      this.goalsNotWorked.length < 1
    );
  }

  @disposeOnUnmount
  public stepOneAutorun = autorun(() => {
    if (this.goalsNotWorked) {
      this.loadingCount = 4;
    }
  });
  @computed get independentActivities() {
    return this.props.checkInStore.independentActivities;
  }

  @action.bound
  public updateCheckIn = (goalId: string, value: any) => {
    this.props.checkInStore.activeCheckIn.data =
      this.props.checkInStore.activeCheckIn.data.map((item: any) => {
        if (item.id === goalId) {
          if (value.type === "task") {
            item.tasks = item.tasks.filter(
              (task: IActivity) => task.id !== value.id
            );
            item.tasks = [...item.tasks, value].flat(2);
          } else if (value.type === "routine") {
            item.routines = item.routines.filter(
              (routine: IActivity) => routine.id !== value.id
            );
            item.routines = [...item.routines, value].flat(2);
          }
        } else {
          item.tasks = item.tasks.filter(
            (task: IActivity) => task.id !== value.id
          );
          item.routines = item.routines.filter(
            (routine: IActivity) => routine.id !== value.id
          );
        }
        return item; // Ensure a value is returned at the end
      });
  };

  @action.bound
  public closeNewActivityModal() {
    this.props.activityStore.createActivityModal = false;
  }

  @action.bound
  public updateGoalCheckIn = (goalId: string, value: any) => {
    this.props.checkInStore.activeCheckIn.data =
      this.props.checkInStore.activeCheckIn.data.map((item: any) => {
        if (item.id === goalId) {
          item = { ...item, ...value };
        }
        return item;
      });
  };

  public renderNullExplainer = () => {
    if (this.emptyGoals) {
      return (
        <div className="center-text">
          <br />
          <Alert
            message="Nice! It looks like you progressed on everything this week. Proceed to complete your check-in."
            type="success"
            showIcon
            className="inline-alert center"
          />
        </div>
      );
    }
  };

  public renderSubtitle = () => {
    if (!this.emptyGoals) {
      return (
        <p className="subTitle-review">
          That’s okay! We can’t make progress on everything, but it’s important
          to evaluate what's still important to us.
        </p>
      );
    }
  };

  public renderLoading = () => {
    if (this.loadingCount < 3) {
      return (
        <div className="check-in-card-wrapper">
          <Card className="goal-review-card" loading />
        </div>
      );
    }
  };

  @action.bound
  public closeActivityModal() {
    this.props.activityStore.updateActivityModal = false;
  }

  @action.bound
  public closeGoalModal() {
    this.props.goalStore.updateGoalModal = false;
  }

  public renderExtraActivities = () => {
    if (this.independentActivities.length > 0) {
      const sortedTasks = () => {
        const tasks = this.independentActivities.filter(
          (activity: IActivity) => activity.type === "task"
        );

        // Separate tasks by completionType
        const scheduledTasks = tasks.filter(
          (task: any) =>
            task.completionType === "scheduled" && task.status !== "complete"
        );
        const soonTasks = tasks.filter(
          (task: any) =>
            task.completionType === "soon" && task.status !== "complete"
        );
        const somedayTasks = tasks.filter(
          (task: any) =>
            task.completionType === "someday" && task.status !== "complete"
        );
        const completedTasks = tasks.filter(
          (task: any) => task.status === "complete"
        );

        // Sort each category of tasks
        scheduledTasks.sort((a: any, b: any) =>
          moment(a.completionDate).diff(moment(b.completionDate))
        );
        soonTasks.sort((a: any, b: any) =>
          moment(a.createdAt).diff(moment(b.createdAt))
        );
        somedayTasks.sort((a: any, b: any) =>
          moment(a.createdAt).diff(moment(b.createdAt))
        );
        completedTasks.sort((a: any, b: any) =>
          moment(a.updatedAt).diff(moment(b.updatedAt))
        );

        // Combine sorted tasks into one array, with incomplete tasks first
        return [
          ...scheduledTasks,
          ...soonTasks,
          ...somedayTasks,
          ...completedTasks,
        ];
      };
      const sortedRoutines = () => {
        const routines = this.independentActivities.filter(
          (activity: IActivity) => activity.type === "routine"
        );

        // Sort routines by frequency
        routines.sort((a: any, b: any) => b.frequency - a.frequency);

        return routines;
      };
      return (
        <div style={{ marginTop: "2rem" }}>
          <div className="center-text">
            <h3 className="step-title">
              And here is everything else you worked on
            </h3>
            <p className="step-description">
              Showing task & routines without goal assigned to them.
            </p>
          </div>

          <div className="check-in-card-wrapper" key="independent-activities">
            {sortedRoutines().map((activity: IActivity) => {
              return (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  goalName=""
                  historicLookup={this.props.checkIn.endDate}
                  showFlag={true}
                />
              );
            })}
            <hr />
            {sortedTasks().map((activity: IActivity) => {
              return (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  goalName=""
                  historicLookup={this.props.checkIn.endDate}
                  showFlag={true}
                />
              );
            })}
          </div>
        </div>
      );
    }
  };

  render() {
    return (
      <div className="step-two">
        <div className="center-text">
          <h3 className="title-review">
            Here are the goals you didn’t work on this week.
          </h3>
          {this.renderSubtitle()}
        </div>
        {this.renderNullExplainer()}
        {this.renderLoading()}

        {this.goalsNotWorked &&
          this.goalsNotWorked.map((goal: IGoal) => {
            return (
              <div className="check-in-card-wrapper" key={goal.id}>
                <GoalReviewCard
                  goal={goal}
                  loading={this.loadingCount < 3}
                  startDate={this.props.checkIn.startDate}
                  endDate={this.props.checkIn.endDate}
                  checkInStore={this.props.checkInStore}
                />
              </div>
            );
          })}
        {this.renderExtraActivities()}
        <StepNavigation
          checkIn={this.props.checkIn}
          title="When you're ready, head over to Step Three."
          allowRevertStep
          allowIncrementStep
          skipToCheckIn={this.props.skipToCheckIn}
        />
        <IonModal
          cssClass={
            this.props.activityStore.createActivityType === "task"
              ? "activity-modal"
              : "routine-modal"
          }
          isOpen={this.props.activityStore.createActivityModal}
          border-radius="7"
          backdropDismiss={false}
        >
          <div className="ion-padding modal-content overflow-y-scroll">
            <ActivityForm
              closeNewActivityModal={this.closeNewActivityModal}
              updateCheckIn={this.updateCheckIn}
            />
          </div>
        </IonModal>

        <IonModal
          cssClass={
            this.props.activityStore.activeActivity.type === "task"
              ? "activity-modal"
              : "routine-modal"
          }
          isOpen={this.props.activityStore.updateActivityModal}
          border-radius="7"
          backdropDismiss={false}
        >
          <div className="ion-padding modal-content overflow-y-scroll">
            <EditActivityForm
              closeActivityModal={this.closeActivityModal}
              updateCheckIn={this.updateCheckIn}
            />
          </div>
        </IonModal>
        <IonModal
          cssClass={
            this.props.goalStore.activeGoal.status === "complete"
              ? "goal-modal-complete"
              : "goal-modal"
          }
          isOpen={this.props.goalStore.updateGoalModal}
          border-radius="7"
          backdropDismiss={false}
        >
          <div className="ion-padding modal-content overflow-y-scroll">
            <EditGoalForm
              closeGoalModal={this.closeGoalModal}
              updateGoalCheckIn={this.updateGoalCheckIn}
            />
          </div>
        </IonModal>
      </div>
    );
  }
}

export default StepTwo;
